class CartProgressBar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.options = JSON.parse(this.dataset.options);
    this.tiers = (this.options.tiers || []).filter(tier => tier.goal > 0).sort((a, b) => a.goal - b.goal);
    this.tierWidthPercent = 100 / this.tiers.length;
    this.wrapper = this.closest('[data-cart-progress-bar-wrapper]');
    this.progressFill = this.querySelector('[data-tier-fill]');
    this.tierSegment = this.querySelector('[data-tier-segment]');
    this.tierProgress = this.querySelector('[data-tier-progress]');
    this.nextTierText = this.querySelector('[data-next-tier]');
    this.thresholdsContainer = this.querySelector('[data-tier-thresholds]');
    this.currentTierIndex = -1;
    this._processingFreeProducts = false;
    this._observer = null;
    this._initialized = false;
    this.updateProgress(null).then(() => {
      this._initialized = true;
    });
    this.renderThresholdMarkers();
    this.cartUpdatedHandler = event => this.updateProgress(event.detail);
    window.addEventListener('cart:updated', this.cartUpdatedHandler);

    if (this.options.context === 'section') {
      if (this.options.layout === 'bottom' && !document.body.classList.contains('template-product')) {
        this.fixedClass = 'is-fixed';
        this.debouncedScreen = debounce(() => {
          if (window.innerHeight > 480) this.cartProgressBarObserver();
        }, 50);

        this.debouncedScreen();
        window.addEventListener('scroll', this.debouncedScreen);
        window.addEventListener('resize', this.debouncedScreen);
      }
      document.documentElement.style.setProperty('--section-height', `${this.offsetHeight}px`);
    }
  }

  disconnectedCallback() {
    if (this.options.layout === 'bottom' && !document.body.classList.contains('template-product')) {
      window.removeEventListener('scroll', this.debouncedScreen);
      window.removeEventListener('resize', this.debouncedScreen);
    }
    window.removeEventListener('cart:updated', this.cartUpdatedHandler);
    this._observer?.disconnect();
  }

  cartProgressBarObserver() {
    if (this._observer) return;
    this._observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const isTopVisible = entry.boundingClientRect.top <= 0;
        if (isTopVisible) {
          if (this.wrapper.classList.contains(this.fixedClass)) return;
          this.wrapper.classList.add('hiding');
          setTimeout(() => {
            this.wrapper.classList.add(this.fixedClass);
            this.wrapper.classList.remove('hiding');
          }, 500);
          setTimeout(() => {
            document.body.style.paddingBottom = `${this.offsetHeight}px`;
          }, 500);
        }
        else {
          this.wrapper.classList.remove(this.fixedClass);
          document.body.style.paddingBottom = 0;
        }
      });
    });
    this._observer.observe(this.wrapper);
  }

  renderThresholdMarkers() {
    if (!this.thresholdsContainer) return;

    this.thresholdsContainer.innerHTML = '';

    this.tiers.forEach((tier, index) => {
      const position = (index + 1) * this.tierWidthPercent;
      const thresholdDiv = document.createElement('div');
      thresholdDiv.className = 'cart-progress-bar__threshold';
      thresholdDiv.style.left = `${position}%`;
      thresholdDiv.setAttribute('data-threshold', tier.goal);

      if ((this.options.discountType === 'product' || tier.freeShipping) && tier.image) {
        thresholdDiv.setAttribute('data-type', 'icon');
        const img = document.createElement('img');
        img.src = tier.image;
        img.alt = '';
        img.className = 'cart-progress-bar__threshold-icon';
        img.width = 24;
        img.height = 24;
        thresholdDiv.appendChild(img);
      }
      else if (this.options.discountType === 'percentage' && tier.discount > 0) {
        thresholdDiv.setAttribute('data-discount', tier.discount);
      }

      this.thresholdsContainer.appendChild(thresholdDiv);
    });
  }

  getCompletedTierIndex(currentTotal) {
    const index = this.tiers.findLastIndex(tier => currentTotal >= tier.goal);
    return index;
  }

  getNextTierIndex(completedTierIndex) {
    if (completedTierIndex < 0) return 0;
    return Math.min(completedTierIndex + 1, this.tiers.length - 1);
  }

  getTierProductId(tier) {
    if (!tier || tier.freeShipping || this.options.discountType !== 'product') return null;
    return tier.productId || null;
  }

  isFreeProduct(item) {
    if (!item.properties) return false;
    const prop = item.properties._free_product;
    return prop === true || prop === 'true';
  }

  isProductMatch(item, productId) {
    const targetId = String(productId);
    return String(item.variant_id) === targetId || String(item.product_id) === targetId;
  }

  calculateProgress(currentTotal, completedTierIndex) {
    if (completedTierIndex < 0) {
      return (Math.min(currentTotal / this.tiers[0].goal, 1)) * this.tierWidthPercent;
    }

    if (completedTierIndex >= this.tiers.length - 1) return 100;

    const completedTiersWidth = (completedTierIndex + 1) * this.tierWidthPercent;
    const tierStart = this.tiers[completedTierIndex].goal;
    const tierGoal = this.tiers[completedTierIndex + 1].goal;
    const tierRange = tierGoal - tierStart;

    if (tierRange <= 0) return completedTiersWidth;

    const progressInTier = Math.max(0, Math.min(currentTotal - tierStart, tierRange));
    return Math.min(completedTiersWidth + (progressInTier / tierRange) * this.tierWidthPercent, 100);
  }

  async updateProgress(cartData = null) {
    try {
      const cart = cartData ?? await this.fetchCart();
      if (!cart) return;

      const currentTotal = cart.total_price;
      const completedTierIndex = this.getCompletedTierIndex(currentTotal);
      const currentTier = this.tiers[completedTierIndex] ?? null;
      const nextTierIndex = this.getNextTierIndex(completedTierIndex);
      const nextTier = this.tiers[nextTierIndex] ?? null;

      await this.handleFreeProducts(completedTierIndex, cart);

      if (!this.tiers?.length) return;

      this.updateProgressBars(currentTotal, completedTierIndex);
      this.updateTextDisplay(currentTotal, completedTierIndex, currentTier, nextTier);
    }
    catch (error) {
      console.error('Error updating cart progress:', error);
    }
  }

  async fetchCart() {
    const response = await fetch(window.routes.cart_url);
    return response.ok ? response.json() : null;
  }

  async handleFreeProducts(completedTierIndex, cart) {
    if (this.options.discountType !== 'product' || this._processingFreeProducts) return;

    const rewardsType = this.options.rewardsType || 'exclusive';
    const freeProductsInCart = cart.items.filter(item => this.isFreeProduct(item));
    const matchedKeys = new Set();

    const productsToAdd = [];
    const productsToRemove = [];

    this.tiers.forEach((tier, tierIndex) => {
      const productId = this.getTierProductId(tier);
      if (!productId) return;

      const isEligible = this.isTierEligible(tierIndex, completedTierIndex, rewardsType);
      const productInCart = freeProductsInCart.find(item => this.isProductMatch(item, productId));

      if (productInCart) matchedKeys.add(productInCart.key);

      if (isEligible && !productInCart) {
        productsToAdd.push({ tierIndex, productId });
      }
      else if (!isEligible && productInCart) {
        productsToRemove.push(productInCart);
      }
    });

    freeProductsInCart.forEach(item => {
      if (!matchedKeys.has(item.key)) productsToRemove.push(item);
    });

    if (!productsToRemove.length && !productsToAdd.length) return;

    this._processingFreeProducts = true;
    try {
      let success = true;
      if (productsToRemove.length) success = (await this.removeFreeProducts(productsToRemove)) && success;
      if (productsToAdd.length) success = (await this.addFreeProducts(productsToAdd)) && success;
      if (this._initialized && success) this.updateCartUI();
    }
    finally {
      this._processingFreeProducts = false;
    }

    this.currentTierIndex = completedTierIndex;
  }

  isTierEligible(tierIndex, completedTierIndex, rewardsType) {
    if (completedTierIndex < 0 || tierIndex > completedTierIndex) return false;
    return rewardsType === 'cumulative' || tierIndex === completedTierIndex;
  }

  updateProgressBars(currentTotal, completedTierIndex) {
    const currentWorkingTier = completedTierIndex < 0 ? 0 : Math.min(completedTierIndex + 1, this.tiers.length - 1);
    const overallProgress = this.calculateProgress(currentTotal, completedTierIndex);
    const tierSegmentWidth = (currentWorkingTier + 1) * this.tierWidthPercent;
    const tierProgressStart = currentWorkingTier * this.tierWidthPercent;

    let tierProgressWidth = 0;
    if (currentWorkingTier < this.tiers.length) {
      const tierStart = currentWorkingTier === 0 ? 0 : this.tiers[currentWorkingTier - 1].goal;
      const tierGoal = this.tiers[currentWorkingTier].goal;
      const tierRange = tierGoal - tierStart;

      if (tierRange > 0) {
        const progressInTier = Math.max(0, Math.min(currentTotal - tierStart, tierRange));
        tierProgressWidth = (progressInTier / tierRange) * this.tierWidthPercent;
      }
    }

    if (this.progressFill) this.progressFill.style.width = `${overallProgress}%`;

    if (this.tierSegment) {
      this.tierSegment.style.left = '0%';
      this.tierSegment.style.width = `${tierSegmentWidth}%`;
      this.tierSegment.style.display = this.tierWidthPercent > 0 ? '' : 'none';
    }

    if (this.tierProgress && this.tierWidthPercent > 0) {
      this.tierProgress.style.width = `${tierProgressWidth + tierProgressStart}%`;
      document.documentElement.style.setProperty('--shipping-width', `${tierProgressWidth + tierProgressStart}%`);
    }
  }

  updateTextDisplay(currentTotal, completedTierIndex, currentTier, nextTier) {
    if (!this.amountNeeded && !this.nextTierText) return;

    const isAtMaxTier = completedTierIndex >= this.tiers.length - 1;
    const hasReachedTier = completedTierIndex >= 0;

    if (isAtMaxTier) {
      if (this.amountNeeded) this.amountNeeded.textContent = '';
      if (this.nextTierText) this.nextTierText.textContent = this.buildReachedMessage(currentTier);
      return;
    }

    const amountNeeded = nextTier.goal - currentTotal;
    const amountNeededText = this.formatMoney(amountNeeded);
    const amountNeededHtml = `<span class="cart-progress-bar__amount-needed">${amountNeededText}</span>`;

    if (this.amountNeeded) this.amountNeeded.textContent = amountNeededText;

    if (this.nextTierText) {
      const nextMessage = this.buildNextTierMessage(nextTier, amountNeededHtml);
      this.nextTierText.innerHTML = hasReachedTier
        ? [this.buildReachedMessage(currentTier), nextMessage].filter(Boolean).join(' ')
        : nextMessage;
    }
  }

  buildReachedMessage(tier) {
    if (!tier) return '';
    if (tier.freeShipping) return this.options.textLabelsFreeShippingReached;
    if (this.options.discountType === 'product' && tier.productName) {
      return this.options.textLabelsFreeProductReached.replace('[[product]]', tier.productName);
    }
    if (this.options.discountType === 'percentage' && tier.discount > 0) {
      return this.options.textLabelsMaximumTierReached.replace('[[max]]', tier.discount);
    }
    return '';
  }

  buildNextTierMessage(tier, amountNeededHtml) {
    if (!tier) return '';
    if (tier.freeShipping) {
      return this.options.textLabelsFreeShippingNeededForNextTier.replace('[[amount_needed]]', amountNeededHtml);
    }
    if (this.options.discountType === 'product') {
      return this.options.textLabelsProductNeededForNextTier
        .replace('[[amount_needed]]', amountNeededHtml)
        .replace('[[product]]', tier.productName ?? '');
    }
    if (this.options.discountType === 'percentage' && tier.discount > 0) {
      return this.options.textLabelsNeededForNextTier
        .replace('[[next_tier]]', tier.discount)
        .replace('[[amount_needed]]', amountNeededHtml);
    }
    return '';
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents).replace(',00', ',-').replace('EUR', '');
    }
    return `${window.Shopify?.currency?.active ?? '€'}${(cents / 100).toFixed(2)}`;
  }

  async removeFreeProducts(items) {
    if (!items.length) return true;
    try {
      const updates = Object.fromEntries(items.map(item => [item.key, 0]));
      const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ updates }) });
      if (!response.ok) throw new Error(response.status);
      return true;
    }
    catch (error) {
      console.error('Error removing free products:', error);
      return false;
    }
  }

  async addFreeProducts(productsToAdd) {
    try {
      const cartItems = document.querySelector('cart-items');
      const items = productsToAdd.map(({ productId }) => ({
        id: productId,
        quantity: 1,
        properties: { _free_product: true },
      }));

      const body = JSON.stringify({
        items,
        sections: cartItems?.sectionsToFetch || [],
        sections_url: window.location.pathname,
      });

      const response = await fetch(routes.cart_add_url, { ...fetchConfig(), body });
      if (!response.ok) throw new Error(response.status);
      return true;
    }
    catch (error) {
      console.error('Error adding free products:', error);
      return false;
    }
  }

  updateCartUI() {
    if (!CartProgressBar._debouncedRenderCart) {
      CartProgressBar._debouncedRenderCart = debounce(() => {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartItems = document.querySelector('cart-items');
        if (cartDrawer) {
          cartDrawer.renderCartDrawer();
        }
        else if (cartItems) {
          fetch(window.routes.cart_url)
            .then(response => response.ok ? response.json() : null)
            .then(cart => cart && cartItems.updateContent(cart, null, null, true));
        }
      }, 150);
    }
    CartProgressBar._debouncedRenderCart();
  }
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar);
}